import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ThemeProvider,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
} from "@mui/material";

import { Edit, Delete } from "@mui/icons-material";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import {
  getCacheTechnicians,
  getTechnician,
  putTechnician,
  putCacheTechnician,
} from "../API/technicianAPI";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { Add as AddIcon } from "@mui/icons-material";

import { createTheme } from "@mui/material/styles";
import BaseWrapper from "../../components/shared/BaseWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function ManageTechnicians() {
  const navigate = useNavigate();
  const [techniciansCache, setTechniciansCache] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null); // Default null to represent no selection
  const [technician, setTechnician] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [isFetchingTechnician, setIsFetchingTechnician] = useState(false); // Prevent double fetch for technician
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [hasNameChanged, setHasNameChanged] = useState(false); // New state variable
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const competencyLabels = [
    { label: "General Pest Control", key: "general_pest_control" },
    { label: "Termite Control", key: "termite_control" },
    { label: "QPMS", key: "qpms" },
    { label: "Disinfection", key: "disinfection" },
    { label: "Basic Safety", key: "basic_safety" },
    { label: "Driving (2 wheels)", key: "driving_2_wheels" },
    { label: "Driving (4 wheels)", key: "driving_4_wheels" },
  ];

  const professionalLabels = [
    {
      category: "GPC",
      items: [
        { label: "Rodent", key: "rodent" },
        { label: "Baiting", key: "baiting" },
        { label: "Cockroach", key: "cockroach" },
        { label: "Fly Control", key: "fly_control" },
        { label: "Ant Control", key: "ant_control" },
        { label: "Larviciding", key: "larviciding" },
      ],
    },
    {
      category: "Termite",
      items: [
        { label: "Compre", key: "compre" },
        { label: "AG", key: "ag" },
        { label: "IG", key: "ig" },
        { label: "IC", key: "ic" },
        { label: "Spraying", key: "spraying" },
        { label: "Soil Poisoning", key: "soil_poisoning" },
      ],
    },
  ];

  // Fetch cache technicians on component mount
  useEffect(() => {
    const fetchCacheTechnicians = async () => {
      try {
        const techniciansCache = await getCacheTechnicians();
        setTechniciansCache(techniciansCache || []); // Ensure it defaults to an empty array if null
      } catch (err) {
        setError("Failed to fetch technicians cache");
      }
    };

    fetchCacheTechnicians();
  }, []); // Empty dependency array ensures this only runs once

  const handleSearchTechnician = (event, value) => {
    setIsFormVisible(false);
    setSelectedTechnician(value || null); // Update the selected technician
  };

  const handleSearchButtonClick = async () => {
    if (!selectedTechnician) {
      console.log("No technician selected");
      return;
    }

    if (isFetchingTechnician) {
      console.log("Already fetching technician details...");
      return;
    }

    setIsFetchingTechnician(true);
    setIsLoading(true);

    try {
      const fetchedTechnician = await getTechnician(selectedTechnician.id);
      setTechnician(fetchedTechnician);
      setIsFormVisible(true);
    } catch (error) {
      console.error("Error fetching technician details:", error);
    } finally {
      setIsFetchingTechnician(false);
      setIsLoading(false);
    }
  };

  const options = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const competencyLevels = [
    "N/A",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
  ];
  const professionalLevels = [
    "Team Leader",
    "High",
    "Mid",
    "Basic",
    "For observation",
  ];

  const handleTechnicianChange = (field, value) => {
    setTechnician((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Mark name change if firstname or lastname is updated
    if (field === "firstname" || field === "lastname") {
      setHasNameChanged(true);
    }
  };

  const handleChange = (event) => {
    const { value, checked } = event.target;
    handleTechnicianChange(
      "rest_day",
      checked
        ? [...technician.rest_day, value]
        : technician.rest_day.filter((item) => item !== value)
    );
  };

  const handleCompetencyLevel = (field, value) => {
    setTechnician((prev) => ({
      ...prev,
      competency_level: {
        ...prev.competency_level, // Spread the existing fields in competency_level
        [field]: value, // Update or add the specific field
      },
    }));
  };

  const handleProfessionalLevel = (category, field, value) => {
    setTechnician((prev) => ({
      ...prev,
      professional_level: {
        ...prev.professional_level, // Preserve other categories in professional_level
        [category]: {
          ...prev.professional_level[category], // Preserve other fields in the specific category
          [field]: value, // Update the specific field
        },
      },
    }));
  };

  const handleViewEditButtonClick = () => {
    setIsEdit((prevIsEdit) => !prevIsEdit);
  };

  const [offService, setOffService] = useState({
    date: "",
    reason: "on-leave",
  });
  const [isOffServiceEdit, setIsOffServiceEdit] = useState(false);
  const [originalDate, setOriginalDate] = useState(null);

  const handleOffServiceChange = (event) => {
    const { name, value } = event.target;
    setOffService((prev) => ({ ...prev, [name]: value }));
  };

  const handleOffServiceAddOrUpdate = () => {
    setTechnician((prev) => {
      if (isOffServiceEdit) {
        // Update the existing record
        return {
          ...prev,
          off_service: prev.off_service.map((record) =>
            record.date === originalDate
              ? { date: offService.date, reason: offService.reason }
              : record
          ),
        };
      }

      // Add a new record
      return {
        ...prev,
        off_service: [
          ...prev.off_service,
          { date: offService.date, reason: offService.reason },
        ],
      };
    });

    // Reset the form
    setOffService({ date: "", reason: "on-leave" });
    setIsOffServiceEdit(false);
    setOriginalDate(null);
  };

  const handleOffServiceEdit = (record) => {
    setOffService({ date: record.date, reason: record.reason });
    setIsOffServiceEdit(true);
    setOriginalDate(record.date);
  };

  const handleOffServiceDelete = (date) => {
    setTechnician((prev) => ({
      ...prev,
      off_service: prev.off_service.filter((record) => record.date !== date),
    }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    const {
      timestamp_create,
      timestamp_update,
      ...updatedTechnicianParameters
    } = technician;

    try {
      // Update technician data
      const response = await putTechnician(
        technician.id,
        updatedTechnicianParameters
      );
      console.log("Technician update response:", response);

      if (response) {
        setSuccessMessage("Technician updated successfully!");

        // Check if firstname or lastname has changed
        if (hasNameChanged) {
          const cacheTechnicianParameters = {
            name: `${technician.lastname}, ${technician.firstname}`,
          };

          // Update cache technician data
          const cacheResponse = await putCacheTechnician(
            technician.id,
            cacheTechnicianParameters
          );
          console.log("Cache technician update response:", cacheResponse);

          if (cacheResponse) {
            setSuccessMessage("Technician and cache updated successfully!");

            // Update the local cache to reflect the new name
            setTechniciansCache((prevCache) =>
              prevCache.map((tech) =>
                tech.id === technician.id
                  ? { ...tech, name: cacheTechnicianParameters.name }
                  : tech
              )
            );

            // Update the selectedTechnician state with the new name
            setSelectedTechnician((prevSelected) =>
              prevSelected?.id === technician.id
                ? { ...prevSelected, name: cacheTechnicianParameters.name }
                : prevSelected
            );

            // Reset the flag after cache update
            setHasNameChanged(false);
          } else {
            setErrorMessage(
              "An unexpected error occurred while updating the cache. Please try again later."
            );
          }
        }
      } else {
        setErrorMessage(
          "An unexpected error occurred while updating the Technician. Please try again later."
        );
      }
    } catch (error) {
      setErrorMessage(
        "An error occurred while updating the Technician. Please check your network connection and try again."
      );
      console.error("Error occurred during submission:", error);
    } finally {
      setIsLoading(false);
    }
  }

  return (

    <BaseWrapper
      titleIcon={<EngineeringIcon />}
      title="Operations"
      subTitle="Manage Technicians"
      subTitleRoute={"/manage-technicians"}
      createButtonLabel="Add Technician"
      onClickCreateButton={() => navigate("/add-technician")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid item xs={12}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Autocomplete
              freeSolo
              options={techniciansCache}
              getOptionLabel={(technician) => technician?.name || ""} // Fallback to an empty string
              value={selectedTechnician} // Controlled component
              onChange={(event, newValue) =>
                handleSearchTechnician(event, newValue)
              } // Updates selected value
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Technician Name"
                  variant="outlined"
                  fullWidth
                  error={!!error} // Displays error styling if there is an error
                  helperText={error || ""} // Displays error message
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            direction={{ xs: "column", sm: "row" }}
            container
            alignItems="center"
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick} // Call button handler
              disabled={isLoading} // Disable button while loading
              sx={{
                mb: { xs: 0, md: 0, sm: 2 },
                mr: { xs: 0, md: 3, sm: 3 },
              }}
            >
              {isLoading ? "Searching..." : "Search"}{" "}
              {/* Change button label dynamically */}
            </Button>
            {isFormVisible && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewEditButtonClick} // Call button handler
              >
                {isEdit ? "Lock Record" : "Edit Record"}{" "}
                {/* Change button label dynamically */}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />
      {isFormVisible && technician && (
        <form
          onSubmit={handleSubmit}
          className="technician-form"
          autoComplete="off"
        >
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{ marginRight: "5px" }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                    >
                      Basic Information
                    </Typography>
                  }
                />
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Grid container spacing={2} sx={{ flex: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        name="firstname"
                        label="Firstname"
                        value={technician.firstname}
                        fullWidth
                        onChange={(e) =>
                          handleTechnicianChange(
                            "firstname",
                            e.target.value
                          )
                        }

                        disabled={!isEdit} // Disable if isEdit is false
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="lastname"
                        label="Lastname"
                        value={technician.lastname}
                        fullWidth
                        onChange={(e) =>
                          handleTechnicianChange(
                            "lastname",
                            e.target.value
                          )
                        }

                        disabled={!isEdit} // Disable if isEdit is false
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          id="team-label"

                        >
                          Team
                        </InputLabel>
                        <Select
                          labelId="team-label"
                          name="type"
                          value={technician.team}
                          label="Team"

                          onChange={(e) =>
                            handleTechnicianChange(
                              "team",
                              e.target.value
                            )
                          }
                          disabled={!isEdit} // Disable if isEdit is false
                        >
                          <MenuItem value="Delta">Delta</MenuItem>
                          <MenuItem value="Sigma">Sigma</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel
                          id="team-label"
                        >
                          Team Leader
                        </InputLabel>
                        <Select
                          labelId="team-label"
                          name="type"
                          value={technician.team_leader}
                          label="Team Leader"
                          onChange={(e) =>
                            handleTechnicianChange("team_leader", e.target.value)
                          }
                          disabled={!isEdit} // Disable if isEdit is false
                        >
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <StyledHr />
                    </Grid>

                    <Grid item xs={12}>
                      <CardHeader
                        title={
                          <Typography
                            variant="h6"
                          >
                            Rest Days
                          </Typography>
                        }
                      />
                      <FormGroup>


                        <Grid container flexWrap>
                          {options.map((option) => (
                            <Grid item key={option}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={technician.rest_day.includes(
                                      option
                                    )}
                                    onChange={handleChange}
                                    value={option}
                                  />
                                }
                                label={option}
                                disabled={!isEdit} // Disable if isEdit is false
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormGroup>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                    >
                      Competency Level
                    </Typography>
                  }
                />
                <CardContent>
                  {competencyLabels.map(({ label, key }) => (
                    <Grid
                      container
                      alignItems="center"
                      key={key}
                    >
                      <Grid item xs={3}>
                        <Typography>
                          {label}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  {competencyLevels.includes("N/A") && (
                                    <Button
                                      variant={
                                        technician.competency_level[
                                          key
                                        ] === "N/A"
                                          ? "contained"
                                          : "outlined"
                                      }
                                      onClick={() =>
                                        handleCompetencyLevel(
                                          key,
                                          "N/A"
                                        )
                                      }
                                      color="primary"
                                      sx={{
                                        minWidth: "40px",
                                        height: "40px",
                                        borderRadius: "8px",
                                      }}
                                      disabled={!isEdit} // Disable if isEdit is false
                                    >
                                      N/A
                                    </Button>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={9}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {competencyLevels
                                    .filter((value) => value !== "N/A")
                                    .map((value) => (
                                      <Button
                                        key={value}
                                        variant={
                                          technician.competency_level[
                                            key
                                          ] === value
                                            ? "contained"
                                            : "outlined"
                                        }
                                        onClick={() =>
                                          handleCompetencyLevel(
                                            key,
                                            value
                                          )
                                        }
                                        color="primary"
                                        sx={{
                                          minWidth: "40px",
                                          height: "40px",
                                          borderRadius: "8px",
                                        }}
                                        disabled={!isEdit} // Disable if isEdit is false
                                      >
                                        {value}
                                      </Button>
                                    ))}
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Grid container>
                {professionalLabels.map((category) => (
                  <Grid item xs={12} sm={6} key={category.category}>
                    <Card
                      variant="outlined"
                      sx={{ marginRight: "5px" }}
                    >
                      <CardHeader
                        title={
                          <Typography
                            variant="h6"
                          >
                            Professional Level ({category.category})
                          </Typography>
                        }
                      />
                      <CardContent>
                        {category.items.map((item) => (
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            sx={{ marginBottom: 3 }}
                            key={item.key}
                          >
                            <Grid item xs={3}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  flexWrap: "nowrap",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Grid>
                            <Grid item xs={9}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                {professionalLevels.map((value) => (
                                  <Button
                                    key={value}
                                    variant={
                                      technician.professional_level[
                                        category.category.toLowerCase()
                                      ][item.key] === value
                                        ? "contained"
                                        : "outlined"
                                    }
                                    color="primary"
                                    onClick={() =>
                                      handleProfessionalLevel(
                                        category.category.toLowerCase(),
                                        item.key,
                                        value
                                      )
                                    }
                                    sx={{
                                      minWidth: {
                                        xs: "30px",
                                        sm: "35px",
                                        md: "40px",
                                      },
                                      height: {
                                        xs: "30px",
                                        sm: "35px",
                                        md: "40px",
                                      },
                                      borderRadius: "8px",
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.75rem",
                                        md: "0.75rem",
                                      },
                                    }}
                                    disabled={!isEdit} // Disable if isEdit is false
                                  >
                                    {value}
                                  </Button>
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <Card
                    variant="outlined"
                    sx={{ marginRight: "5px" }}
                  >
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                        >
                          Off Service
                        </Typography>
                      }
                    />
                    <CardContent>
                      <FormGroup>
                        {/* Input Row */}
                        <Grid container spacing={2} alignItems="center">
                          <Grid item>
                            <TextField
                              name="date"
                              type="date"
                              value={offService.date}
                              onChange={handleOffServiceChange}
                              sx={{ width: "200px" }}
                              disabled={!isEdit} // Disable if isEdit is false
                            />
                          </Grid>
                          <Grid item>
                            <Select
                              name="reason"
                              value={offService.reason}
                              onChange={handleOffServiceChange}
                              sx={{ width: "150px" }}
                              disabled={!isEdit} // Disable if isEdit is false
                            >
                              <MenuItem value="on-leave">
                                On-Leave
                              </MenuItem>
                              <MenuItem value="suspended">
                                Suspended
                              </MenuItem>
                            </Select>
                          </Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              onClick={handleOffServiceAddOrUpdate}
                              sx={{ height: "40px" }}
                              disabled={!isEdit} // Disable if isEdit is false
                            >
                              {isOffServiceEdit ? "Update" : "Add"}
                            </Button>
                          </Grid>
                        </Grid>

                        {/* Table Row */}
                        <TableContainer sx={{ marginTop: "2rem" }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {technician.off_service
                                .sort(
                                  (a, b) =>
                                    new Date(b.date) - new Date(a.date)
                                ) // Sort by date, newest first
                                .map((record, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{record.date}</TableCell>
                                    <TableCell>
                                      {record.reason}
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        onClick={() =>
                                          handleOffServiceEdit(record)
                                        }
                                        disabled={!isEdit} // Disable if isEdit is false
                                      >
                                        <Edit />
                                      </IconButton>
                                      <IconButton
                                        onClick={() =>
                                          handleOffServiceDelete(
                                            record.date
                                          )
                                        }
                                        disabled={!isEdit} // Disable if isEdit is false
                                      >
                                        <Delete />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </FormGroup>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            {isEdit && (
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mt={2}
                mb={2}
                mr={2}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => window.location.reload()}
                  sx={{
                    fontSize: "0.875rem",
                    color: "#FF4C4C",
                    borderColor: "#FF4C4C",
                  }}
                  disabled={isLoading} // Optionally disable the button while loading
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{
                    marginLeft: "10px",
                    fontSize: "0.875rem",
                  }}
                  disabled={isLoading} // Optionally disable the button while loading
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      )}

    </BaseWrapper>

    // <ThemeProvider theme={theme}>
    //   {/* Success Snackbar */}
    //   <Snackbar
    //     open={!!successMessage}
    //     autoHideDuration={3000}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setSuccessMessage("")} // Clear the success message on close
    //   >
    //     <Alert
    //       severity="success"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {successMessage}
    //     </Alert>
    //   </Snackbar>

    //   {/* Loading Snackbar */}
    //   <Snackbar
    //     open={isLoading}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //   >
    //     <Alert
    //       severity="info"
    //       icon={<CircularProgress size={18} />}
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       Loading, please wait...
    //     </Alert>
    //   </Snackbar>

    //   {/* Error Snackbar */}
    //   <Snackbar
    //     open={!!errorMessage}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setErrorMessage("")}
    //   >
    //     <Alert
    //       severity="error"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {errorMessage}
    //     </Alert>
    //   </Snackbar>
    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <EngineeringIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Operations</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>
    //                 Operations
    //               </Typography>
    //               <Typography
    //                 component={Link}
    //                 to="/manage-technicians"
    //                 sx={{ color: "#ffffff", textDecoration: "none" }}
    //               >
    //                 Manage Technicians
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />
    //       <CardContent sx={{ padding: "20px" }}>
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Manage Technicians
    //               </Typography>
    //             }
    //             action={
    //               <Button
    //                 component={Link}
    //                 onClick={() => navigate("/add-technician")}
    //                 variant="contained"
    //                 color="primary"
    //                 startIcon={<AddIcon />}
    //               >
    //                 Add Technician
    //               </Button>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />
    //           <Grid item xs={12}>
    //             <Grid container spacing={3} sx={{ mb: 2 }}>
    //               <Grid item xs={4}>
    //                 <Autocomplete
    //                   freeSolo
    //                   options={techniciansCache}
    //                   getOptionLabel={(technician) => technician?.name || ""} // Fallback to an empty string
    //                   value={selectedTechnician} // Controlled component
    //                   onChange={(event, newValue) =>
    //                     handleSearchTechnician(event, newValue)
    //                   } // Updates selected value
    //                   renderInput={(params) => (
    //                     <TextField
    //                       {...params}
    //                       label="Technician Name"
    //                       variant="outlined"
    //                       fullWidth
    //                       error={!!error} // Displays error styling if there is an error
    //                       helperText={error || ""} // Displays error message
    //                     />
    //                   )}
    //                 />
    //               </Grid>
    //               <Grid
    //                 item
    //                 xs={12}
    //                 sm={6}
    //                 direction={{ xs: "column", sm: "row" }}
    //                 container
    //                 alignItems="center"
    //               >
    //                 <Button
    //                   variant="contained"
    //                   color="primary"
    //                   onClick={handleSearchButtonClick} // Call button handler
    //                   disabled={isLoading} // Disable button while loading
    //                   sx={{
    //                     mb: { xs: 0, md: 0, sm: 2 },
    //                     mr: { xs: 0, md: 3, sm: 3 },
    //                   }}
    //                 >
    //                   {isLoading ? "Searching..." : "Search"}{" "}
    //                   {/* Change button label dynamically */}
    //                 </Button>
    //                 {isFormVisible && (
    //                   <Button
    //                     variant="contained"
    //                     color="primary"
    //                     onClick={handleViewEditButtonClick} // Call button handler
    //                   >
    //                     {isEdit ? "Lock Record" : "Edit Record"}{" "}
    //                     {/* Change button label dynamically */}
    //                   </Button>
    //                 )}
    //               </Grid>
    //             </Grid>
    //           </Grid>
    //           <StyledHr />
    //           {isFormVisible && technician && (
    //             <form
    //               onSubmit={handleSubmit}
    //               className="technician-form"
    //               autoComplete="off"
    //             >
    //               <Grid container spacing={3}>
    //                 <Grid
    //                   item
    //                   xs={12}
    //                   md={6}
    //                   sx={{ display: "flex", flexDirection: "column" }}
    //                 >
    //                   <Card
    //                     variant="outlined"
    //                     sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
    //                   >
    //                     <CardHeader
    //                       title={
    //                         <Typography
    //                           variant="h6"
    //                           sx={{
    //                             paddingBottom: "1rem",
    //                             fontWeight: "bold",
    //                             textTransform: "uppercase",
    //                             fontSize: "1rem",
    //                           }}
    //                         >
    //                           Basic Information
    //                         </Typography>
    //                       }
    //                     />
    //                     <CardContent
    //                       sx={{
    //                         flex: 1,
    //                         display: "flex",
    //                         flexDirection: "column",
    //                       }}
    //                     >
    //                       <Grid container spacing={3} sx={{ flex: 1 }}>
    //                         <Grid item xs={6}>
    //                           <TextField
    //                             name="firstname"
    //                             label="Firstname"
    //                             value={technician.firstname}
    //                             fullWidth
    //                             onChange={(e) =>
    //                               handleTechnicianChange(
    //                                 "firstname",
    //                                 e.target.value
    //                               )
    //                             }
    //                             size="small"
    //                             InputProps={{ sx: { fontSize: "0.875rem" } }}
    //                             InputLabelProps={{
    //                               sx: { fontSize: "0.875rem" },
    //                             }}
    //                             disabled={!isEdit} // Disable if isEdit is false
    //                           />
    //                         </Grid>
    //                         <Grid item xs={6}>
    //                           <TextField
    //                             name="lastname"
    //                             label="Lastname"
    //                             value={technician.lastname}
    //                             fullWidth
    //                             onChange={(e) =>
    //                               handleTechnicianChange(
    //                                 "lastname",
    //                                 e.target.value
    //                               )
    //                             }
    //                             size="small"
    //                             InputProps={{ sx: { fontSize: "0.875rem" } }}
    //                             InputLabelProps={{
    //                               sx: { fontSize: "0.875rem" },
    //                             }}
    //                             disabled={!isEdit} // Disable if isEdit is false
    //                           />
    //                         </Grid>
    //                         <Grid item xs={6}>
    //                           <FormControl fullWidth size="small">
    //                             <InputLabel
    //                               id="team-label"
    //                               sx={{ fontSize: "0.875rem" }}
    //                             >
    //                               Team
    //                             </InputLabel>
    //                             <Select
    //                               labelId="team-label"
    //                               name="type"
    //                               value={technician.team}
    //                               label="Team"
    //                               sx={{ fontSize: "0.875rem" }}
    //                               onChange={(e) =>
    //                                 handleTechnicianChange(
    //                                   "team",
    //                                   e.target.value
    //                                 )
    //                               }
    //                               disabled={!isEdit} // Disable if isEdit is false
    //                             >
    //                               <MenuItem value="Delta">Delta</MenuItem>
    //                               <MenuItem value="Sigma">Sigma</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </Grid>
    //                         <Grid item xs={6}>
    //                           <FormControl fullWidth size="small">
    //                             <InputLabel
    //                               id="team-label"
    //                               sx={{ fontSize: "0.875rem" }}
    //                             >
    //                               Team Leader
    //                             </InputLabel>
    //                             <Select
    //                               labelId="team-label"
    //                               name="type"
    //                               value={technician.team_leader}
    //                               label="Team Leader"
    //                               sx={{ fontSize: "0.875rem" }}
    //                               onChange={(e) =>
    //                                 handleTechnicianChange("team_leader", e.target.value)
    //                               }
    //                               disabled={!isEdit} // Disable if isEdit is false
    //                             >
    //                               <MenuItem value="yes">Yes</MenuItem>
    //                               <MenuItem value="no">No</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </Grid>

    //                         <Grid item xs={12}>
    //                           <StyledHr />
    //                         </Grid>

    //                         <Grid item xs={12}>
    //                           <FormGroup>
    //                             <Typography
    //                               variant="h6"
    //                               sx={{
    //                                 fontWeight: "bold",
    //                                 textTransform: "uppercase",
    //                                 fontSize: "1rem",
    //                                 marginBottom: "2rem",
    //                               }}
    //                             >
    //                               Rest Days
    //                             </Typography>

    //                             <Grid container spacing={2} flexWrap>
    //                               {options.map((option) => (
    //                                 <Grid item key={option}>
    //                                   <FormControlLabel
    //                                     control={
    //                                       <Checkbox
    //                                         checked={technician.rest_day.includes(
    //                                           option
    //                                         )}
    //                                         onChange={handleChange}
    //                                         value={option}
    //                                       />
    //                                     }
    //                                     label={option}
    //                                     disabled={!isEdit} // Disable if isEdit is false
    //                                   />
    //                                 </Grid>
    //                               ))}
    //                             </Grid>
    //                           </FormGroup>
    //                         </Grid>
    //                       </Grid>
    //                     </CardContent>
    //                   </Card>
    //                 </Grid>

    //                 <Grid item xs={12} md={6}>
    //                   <Card
    //                     variant="outlined"
    //                     sx={{ padding: "20px", marginBottom: "20px" }}
    //                   >
    //                     <CardHeader
    //                       title={
    //                         <Typography
    //                           variant="h6"
    //                           sx={{
    //                             paddingBottom: "1rem",
    //                             fontWeight: "bold",
    //                             textTransform: "uppercase",
    //                             fontSize: "1rem",
    //                           }}
    //                         >
    //                           Competency Level
    //                         </Typography>
    //                       }
    //                     />
    //                     <CardContent>
    //                       {competencyLabels.map(({ label, key }) => (
    //                         <Grid
    //                           container
    //                           spacing={2}
    //                           alignItems="center"
    //                           sx={{ marginBottom: 3 }}
    //                           key={key}
    //                         >
    //                           <Grid item xs={3}>
    //                             <Typography variant="subtitle1" gutterBottom>
    //                               {label}
    //                             </Typography>
    //                           </Grid>
    //                           <Grid item xs={9}>
    //                             <Grid container spacing={3}>
    //                               <Grid item xs={12}>
    //                                 <Grid container spacing={2}>
    //                                   <Grid item xs={3}>
    //                                     <Box
    //                                       sx={{
    //                                         display: "flex",
    //                                         justifyContent: "center",
    //                                       }}
    //                                     >
    //                                       {competencyLevels.includes("N/A") && (
    //                                         <Button
    //                                           variant={
    //                                             technician.competency_level[
    //                                               key
    //                                             ] === "N/A"
    //                                               ? "contained"
    //                                               : "outlined"
    //                                           }
    //                                           onClick={() =>
    //                                             handleCompetencyLevel(
    //                                               key,
    //                                               "N/A"
    //                                             )
    //                                           }
    //                                           color="primary"
    //                                           sx={{
    //                                             minWidth: "40px",
    //                                             height: "40px",
    //                                             borderRadius: "8px",
    //                                           }}
    //                                           disabled={!isEdit} // Disable if isEdit is false
    //                                         >
    //                                           N/A
    //                                         </Button>
    //                                       )}
    //                                     </Box>
    //                                   </Grid>
    //                                   <Grid item xs={9}>
    //                                     <Box
    //                                       sx={{
    //                                         display: "flex",
    //                                         gap: 1,
    //                                         flexWrap: "wrap",
    //                                       }}
    //                                     >
    //                                       {competencyLevels
    //                                         .filter((value) => value !== "N/A")
    //                                         .map((value) => (
    //                                           <Button
    //                                             key={value}
    //                                             variant={
    //                                               technician.competency_level[
    //                                                 key
    //                                               ] === value
    //                                                 ? "contained"
    //                                                 : "outlined"
    //                                             }
    //                                             onClick={() =>
    //                                               handleCompetencyLevel(
    //                                                 key,
    //                                                 value
    //                                               )
    //                                             }
    //                                             color="primary"
    //                                             sx={{
    //                                               minWidth: "40px",
    //                                               height: "40px",
    //                                               borderRadius: "8px",
    //                                             }}
    //                                             disabled={!isEdit} // Disable if isEdit is false
    //                                           >
    //                                             {value}
    //                                           </Button>
    //                                         ))}
    //                                     </Box>
    //                                   </Grid>
    //                                 </Grid>
    //                               </Grid>
    //                             </Grid>
    //                           </Grid>
    //                         </Grid>
    //                       ))}
    //                     </CardContent>
    //                   </Card>
    //                 </Grid>

    //                 <Grid item xs={12}>
    //                   <Grid container spacing={3}>
    //                     {professionalLabels.map((category) => (
    //                       <Grid item xs={12} sm={6} key={category.category}>
    //                         <Card
    //                           variant="outlined"
    //                           sx={{ padding: "20px", marginBottom: "20px" }}
    //                         >
    //                           <CardHeader
    //                             title={
    //                               <Typography
    //                                 variant="h6"
    //                                 sx={{
    //                                   paddingBottom: "1rem",
    //                                   fontWeight: "bold",
    //                                   textTransform: "uppercase",
    //                                   fontSize: "1rem",
    //                                 }}
    //                               >
    //                                 Professional Level ({category.category})
    //                               </Typography>
    //                             }
    //                           />
    //                           <CardContent>
    //                             {category.items.map((item) => (
    //                               <Grid
    //                                 container
    //                                 spacing={2}
    //                                 alignItems="center"
    //                                 sx={{ marginBottom: 3 }}
    //                                 key={item.key}
    //                               >
    //                                 <Grid item xs={3}>
    //                                   <Typography
    //                                     variant="subtitle1"
    //                                     gutterBottom
    //                                     sx={{
    //                                       overflow: "hidden",
    //                                       textOverflow: "ellipsis",
    //                                       flexWrap: "nowrap",
    //                                     }}
    //                                   >
    //                                     {item.label}
    //                                   </Typography>
    //                                 </Grid>
    //                                 <Grid item xs={9}>
    //                                   <Box
    //                                     sx={{
    //                                       display: "flex",
    //                                       gap: 1,
    //                                       flexWrap: "wrap",
    //                                     }}
    //                                   >
    //                                     {professionalLevels.map((value) => (
    //                                       <Button
    //                                         key={value}
    //                                         variant={
    //                                           technician.professional_level[
    //                                             category.category.toLowerCase()
    //                                           ][item.key] === value
    //                                             ? "contained"
    //                                             : "outlined"
    //                                         }
    //                                         color="primary"
    //                                         onClick={() =>
    //                                           handleProfessionalLevel(
    //                                             category.category.toLowerCase(),
    //                                             item.key,
    //                                             value
    //                                           )
    //                                         }
    //                                         sx={{
    //                                           minWidth: {
    //                                             xs: "30px",
    //                                             sm: "35px",
    //                                             md: "40px",
    //                                           },
    //                                           height: {
    //                                             xs: "30px",
    //                                             sm: "35px",
    //                                             md: "40px",
    //                                           },
    //                                           borderRadius: "8px",
    //                                           fontSize: {
    //                                             xs: "0.75rem",
    //                                             sm: "0.75rem",
    //                                             md: "0.75rem",
    //                                           },
    //                                         }}
    //                                         disabled={!isEdit} // Disable if isEdit is false
    //                                       >
    //                                         {value}
    //                                       </Button>
    //                                     ))}
    //                                   </Box>
    //                                 </Grid>
    //                               </Grid>
    //                             ))}
    //                           </CardContent>
    //                         </Card>
    //                       </Grid>
    //                     ))}
    //                   </Grid>
    //                 </Grid>

    //                 <Grid item xs={12}>
    //                   <Grid container spacing={3}>
    //                     <Grid item xs={12} sm={6}>
    //                       <Card
    //                         variant="outlined"
    //                         sx={{ padding: "20px", marginBottom: "20px" }}
    //                       >
    //                         <CardHeader
    //                           title={
    //                             <Typography
    //                               variant="h6"
    //                               sx={{
    //                                 paddingBottom: "1rem",
    //                                 fontWeight: "bold",
    //                                 textTransform: "uppercase",
    //                                 fontSize: "1rem",
    //                               }}
    //                             >
    //                               Off Service
    //                             </Typography>
    //                           }
    //                         />
    //                         <CardContent>
    //                           <FormGroup>
    //                             {/* Input Row */}
    //                             <Grid container spacing={2} alignItems="center">
    //                               <Grid item>
    //                                 <TextField
    //                                   name="date"
    //                                   type="date"
    //                                   value={offService.date}
    //                                   onChange={handleOffServiceChange}
    //                                   sx={{ width: "200px" }}
    //                                   disabled={!isEdit} // Disable if isEdit is false
    //                                 />
    //                               </Grid>
    //                               <Grid item>
    //                                 <Select
    //                                   name="reason"
    //                                   value={offService.reason}
    //                                   onChange={handleOffServiceChange}
    //                                   sx={{ width: "150px" }}
    //                                   disabled={!isEdit} // Disable if isEdit is false
    //                                 >
    //                                   <MenuItem value="on-leave">
    //                                     On-Leave
    //                                   </MenuItem>
    //                                   <MenuItem value="suspended">
    //                                     Suspended
    //                                   </MenuItem>
    //                                 </Select>
    //                               </Grid>
    //                               <Grid item>
    //                                 <Button
    //                                   variant="contained"
    //                                   onClick={handleOffServiceAddOrUpdate}
    //                                   sx={{ height: "40px" }}
    //                                   disabled={!isEdit} // Disable if isEdit is false
    //                                 >
    //                                   {isOffServiceEdit ? "Update" : "Add"}
    //                                 </Button>
    //                               </Grid>
    //                             </Grid>

    //                             {/* Table Row */}
    //                             <TableContainer sx={{ marginTop: "2rem" }}>
    //                               <Table>
    //                                 <TableHead>
    //                                   <TableRow>
    //                                     <TableCell>Date</TableCell>
    //                                     <TableCell>Reason</TableCell>
    //                                     <TableCell>Action</TableCell>
    //                                   </TableRow>
    //                                 </TableHead>
    //                                 <TableBody>
    //                                   {technician.off_service
    //                                     .sort(
    //                                       (a, b) =>
    //                                         new Date(b.date) - new Date(a.date)
    //                                     ) // Sort by date, newest first
    //                                     .map((record, index) => (
    //                                       <TableRow key={index}>
    //                                         <TableCell>{record.date}</TableCell>
    //                                         <TableCell>
    //                                           {record.reason}
    //                                         </TableCell>
    //                                         <TableCell>
    //                                           <IconButton
    //                                             onClick={() =>
    //                                               handleOffServiceEdit(record)
    //                                             }
    //                                             disabled={!isEdit} // Disable if isEdit is false
    //                                           >
    //                                             <Edit />
    //                                           </IconButton>
    //                                           <IconButton
    //                                             onClick={() =>
    //                                               handleOffServiceDelete(
    //                                                 record.date
    //                                               )
    //                                             }
    //                                             disabled={!isEdit} // Disable if isEdit is false
    //                                           >
    //                                             <Delete />
    //                                           </IconButton>
    //                                         </TableCell>
    //                                       </TableRow>
    //                                     ))}
    //                                 </TableBody>
    //                               </Table>
    //                             </TableContainer>
    //                           </FormGroup>
    //                         </CardContent>
    //                       </Card>
    //                     </Grid>
    //                   </Grid>
    //                 </Grid>

    //                 {isEdit && (
    //                   <Grid
    //                     item
    //                     xs={12}
    //                     display="flex"
    //                     justifyContent="flex-end"
    //                     alignItems="center"
    //                     mt={2}
    //                     mb={2}
    //                     mr={2}
    //                   >
    //                     <Button
    //                       variant="outlined"
    //                       color="secondary"
    //                       onClick={() => window.location.reload()}
    //                       sx={{
    //                         fontSize: "0.875rem",
    //                         color: "#FF4C4C",
    //                         borderColor: "#FF4C4C",
    //                       }}
    //                       disabled={isLoading} // Optionally disable the button while loading
    //                     >
    //                       Cancel
    //                     </Button>
    //                     <Button
    //                       variant="contained"
    //                       color="primary"
    //                       type="submit"
    //                       sx={{
    //                         marginLeft: "10px",
    //                         fontSize: "0.875rem",
    //                       }}
    //                       disabled={isLoading} // Optionally disable the button while loading
    //                     >
    //                       {isLoading ? "Submitting..." : "Submit"}
    //                     </Button>
    //                   </Grid>
    //                 )}
    //               </Grid>
    //             </form>
    //           )}
    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default ManageTechnicians;
